<script setup>
import LandingFooter from '@/components/landing/LandingFooter.vue'
import { useScheduleStore } from '@/composables/store/guest/useScheduleStore'
import { storeToRefs } from 'pinia'
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'

const scheduleStore = useScheduleStore()
const {
  loading: isSubmitting,
  error: scheduleError,
  availabilitySettings,
  slotsByDate,
  availableSlots,
} = storeToRefs(scheduleStore)

// Import images
import activity from '@images/landing/Activity.png'
import analyticsSample from '@images/landing/Analytics_sample.png'
import appointments from '@images/landing/Appointments.png'
import contractors from '@images/landing/Contractors.png'
import facilityManagement from '@images/landing/FacilityManagement.png'
import slaSingle from '@images/landing/SLASingle.png'
import workOrderManagement from '@images/landing/WorkOrderManagement.png'
import bg from '@images/landing/bg.jpeg'
import logo from '@images/landing/logo.png'

// Set the layout for this page
definePage({ meta: { layout: 'landing' } })

// Constants
const DEFAULT_FEATURE = 'facility'
const INTERSECTION_THRESHOLD = 0.15

// Original Features data
const features = [
  {
    id: 'facility',
    icon: 'i-mdi-home-city-outline',
    title: 'Facility Management',
    description: 'Centralize assets, spaces and maintenance in one clean dashboard for complete visibility.',
    image: facilityManagement,
    alt: 'Facility Management Dashboard',
  },
  {
    id: 'workorders',
    icon: 'i-mdi-tools',
    title: 'Work Order Management',
    description: 'Create, assign, track and close work orders with real-time status and clear priorities.',
    image: workOrderManagement,
    alt: 'Work Order Management',
  },
  {
    id: 'booking',
    icon: 'i-mdi-calendar-check-outline',
    title: 'Appointment & Booking System',
    description: 'Let tenants and teams book assets instantly with smart availability and automated reminders.',
    image: appointments,
    alt: 'Booking & Scheduling',
  },
  {
    id: 'contractor',
    icon: 'i-mdi-account-hard-hat-outline',
    title: 'Contractor Management',
    description: 'Manage contractor profiles, certifications and SLAs so the right vendor is always on the job.',
    image: contractors,
    alt: 'Contractor Management',
  },
  {
    id: 'analytics',
    icon: 'i-mdi-chart-bar',
    title: 'Analytics & Reporting',
    description: 'Visual dashboards for utilization, downtime and SLA trends so you can prioritize investments quickly.',
    image: activity,
    alt: 'Analytics & Reporting',
  },
  {
    id: 'sla',
    icon: 'i-mdi-timer-sand-complete',
    title: 'SLA Configuration',
    description: 'Set response and resolution targets by asset, category or site and monitor performance automatically.',
    image: slaSingle,
    alt: 'SLA Configuration',
  },
]

// Original Benefits data
const benefits = [
  'Centralized facility management',
  'Automated booking and scheduling',
  'Real-time work order tracking',
  'Comprehensive contractor management',
  'Advanced analytics and reporting',
  'SLA monitoring and compliance',
]

// Original Contact points data
const contactPoints = [
  { text: 'Guided product tour focused on your use cases.', color: 'bg-info-light' },
  { text: 'No obligation, no implementation required.', color: 'bg-success-light' },
  { text: 'Recommendations on rollout, change management, and SLAs.', color: 'bg-warning-light' },
]

// Reactive state
const activeFeature = ref(DEFAULT_FEATURE)
const shouldAnimate = ref(false)
const featureCard = ref(null)
const isVisible = ref({
  features: true,
  benefits: false,
  contact: false,
})

// -----------------------------
// Contact modal + meeting form
// -----------------------------
const isContactModalOpen = ref(false)
const submitSuccess = ref('')

const meetingForm = ref({
  name: '',
  email: '',
  start_time_local: '',
  end_time_local: '',
  time_zone: 'Asia/Brunei',
  message: '',
})

// -----------------------------
// Time zone list + searchable combobox
// -----------------------------
const timeZones = ref([])

// combobox state
const tzQuery = ref('') // ✅ USER-FACING label (spaces, no underscores)
const isTzOpen = ref(false)
const tzActiveIndex = ref(-1)
const tzWrapRef = ref(null)
const tzListRef = ref(null)
const TZ_RESULTS_LIMIT = 80

function getAllTimeZones() {
  if (typeof Intl !== 'undefined' && typeof Intl.supportedValuesOf === 'function') {
    try {
      const zones = Intl.supportedValuesOf('timeZone')
      if (Array.isArray(zones) && zones.length) return zones
    } catch (e) { }
  }

  return [
    'UTC',
    'Asia/Singapore',
    'Asia/Brunei',
    'Asia/Kuala_Lumpur',
    'Asia/Jakarta',
    'Asia/Manila',
    'Asia/Bangkok',
    'Asia/Hong_Kong',
    'Asia/Shanghai',
    'Asia/Taipei',
    'Asia/Tokyo',
    'Asia/Seoul',
    'Australia/Perth',
    'Australia/Sydney',
    'Europe/London',
    'Europe/Paris',
    'Europe/Berlin',
    'Europe/Moscow',
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
  ]
}

// ✅ UI label: "Africa/Addis_Ababa" -> "Africa/Addis Ababa"
function formatTimeZoneLabel(tz) {
  return String(tz || '').replace(/_/g, ' ')
}

// ✅ label -> IANA candidate: "Africa/Addis Ababa" -> "Africa/Addis_Ababa"
function labelToIana(label) {
  const s = String(label || '').trim()
  if (!s) return ''
  // collapse whitespace then convert spaces to underscores
  return s.replace(/\s+/g, ' ').replace(/ /g, '_')
}

const filteredTimeZones = computed(() => {
  const q = tzQuery.value.trim().toLowerCase()
  const list = timeZones.value

  if (!list.length) return []
  if (!q) return list.slice(0, TZ_RESULTS_LIMIT)

  const out = []
  for (let i = 0; i < list.length; i++) {
    const tz = list[i]
    const raw = tz.toLowerCase()
    const pretty = formatTimeZoneLabel(tz).toLowerCase()

    // ✅ match against both raw + pretty (so "Addis Ababa" finds "Addis_Ababa")
    if (raw.includes(q) || pretty.includes(q)) {
      out.push(tz)
      if (out.length >= TZ_RESULTS_LIMIT) break
    }
  }
  return out
})

// validate what user typed (search box), not meetingForm.time_zone
const isTypedTimeZoneValid = computed(() => {
  const typed = tzQuery.value?.trim()
  if (!typed) return false

  // allow either exact raw OR label form
  if (timeZones.value.includes(typed)) return true

  const asIana = labelToIana(typed)
  return timeZones.value.includes(asIana)
})

// -----------------------------
// Helpers
// -----------------------------
function pad2(n) {
  return String(n).padStart(2, '0')
}

function toISODate(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
}

function isWeekendISO(iso) {
  if (!iso) return false
  const d = new Date(`${iso}T00:00:00`)
  const day = d.getDay() // Sun=0..Sat=6
  return day === 0 || day === 6
}

function nextWorkingDayISO(from = new Date()) {
  const d = new Date(from.getFullYear(), from.getMonth(), from.getDate())
  for (let i = 0; i < 14; i++) {
    const iso = toISODate(d)
    if (!isWeekendISO(iso)) return iso
    d.setDate(d.getDate() + 1)
  }
  return toISODate(from)
}

// Next date that is in the backend's days_available (0=Sun..6=Sat); falls back to next weekday if no settings
function nextAvailableDayISO(from = new Date()) {
  const days = availabilitySettings.value?.days_available
  if (Array.isArray(days) && days.length > 0) {
    // Backend may return either [0,1,2,3,4,5] or a boolean array [false,true,...].
    const allowed = new Set()
    if (typeof days[0] === 'boolean') {
      days.forEach((val, idx) => {
        if (val) allowed.add(idx)
      })
    } else {
      days.forEach(d => allowed.add(d))
    }
    const d = new Date(from.getFullYear(), from.getMonth(), from.getDate())
    for (let i = 0; i < 14; i++) {
      if (allowed.has(d.getDay())) return toISODate(d)
      d.setDate(d.getDate() + 1)
    }
    return toISODate(d)
  }
  return nextWorkingDayISO(from)
}

// -----------------------------
// Time zone conversion helpers
// -----------------------------
function getTZParts(dateObj, timeZone) {
  const dtf = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
  const parts = dtf.formatToParts(dateObj)
  const map = {}
  for (const p of parts) {
    if (p.type !== 'literal') map[p.type] = p.value
  }
  return {
    year: Number(map.year),
    month: Number(map.month),
    day: Number(map.day),
    hour: Number(map.hour),
    minute: Number(map.minute),
    second: Number(map.second),
    dateISO: `${map.year}-${map.month}-${map.day}`,
    hhmm: `${map.hour}:${map.minute}`,
  }
}

// Offset (minutes) between UTC and the given timeZone *at the given instant*
function tzOffsetMinutes(timeZone, dateObj) {
  const p = getTZParts(dateObj, timeZone)
  const asUTC = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second)
  return (asUTC - dateObj.getTime()) / 60000
}

// Convert a UTC ISO string (with "Z") to local date/time parts + offset in the given time zone
function utcToLocalParts(utcString, timeZone) {
  if (!utcString || !timeZone) return null
  const date = new Date(utcString)
  if (Number.isNaN(date.getTime())) return null
  const parts = getTZParts(date, timeZone)
  const offsetMinutes = tzOffsetMinutes(timeZone, date)
  return { ...parts, offsetMinutes }
}

function formatOffset(minutes) {
  const total = Math.abs(minutes)
  const hours = Math.floor(total / 60)
  const mins = total % 60
  const sign = minutes >= 0 ? '+' : '-'
  return `${sign}${pad2(hours)}:${pad2(mins)}`
}

function hhmmToMinutes(hhmm) {
  const [h, m] = String(hhmm || '').split(':').map(Number)
  if (Number.isNaN(h) || Number.isNaN(m)) return 0
  return h * 60 + m
}

// Convert UTC instant to ISO with numeric offset for a given time zone
function utcToOffsetISO(utcString, timeZone) {
  const parts = utcToLocalParts(utcString, timeZone)
  if (!parts) return ''
  const offset = formatOffset(parts.offsetMinutes)
  return `${parts.dateISO}T${parts.hhmm}:00${offset}`
}

// Build friendly labels and local ISO strings for a backend slot
function formatSlotLabel(utcStart, utcEnd, timeZone) {
  const start = utcToLocalParts(utcStart, timeZone)
  const end = utcToLocalParts(utcEnd, timeZone)
  if (!start || !end) {
    return {
      timeLabel: '',
      dateLabel: '',
      startLocalISO: '',
      endLocalISO: '',
    }
  }
  const offset = formatOffset(start.offsetMinutes)
  return {
    timeLabel: `${start.hhmm}–${end.hhmm}`,
    dateLabel: start.dateISO,
    startLocalISO: `${start.dateISO}T${start.hhmm}:00${offset}`,
    endLocalISO: `${end.dateISO}T${end.hhmm}:00${offset}`,
  }
}

// -----------------------------
// Calendar + backend-driven time-slot picker
// -----------------------------
const today = new Date()
const calMonth = ref(new Date(today.getFullYear(), today.getMonth(), 1))

const selectedDateISO = ref('')
const selectedSlotKey = ref('') // "start|end" identifier for the chosen slot

// Weekday availability from backend (0=Sun..6=Sat)
const allowedWeekdaySet = computed(() => {
  const days = availabilitySettings.value?.days_available
  if (!Array.isArray(days) || !days.length) return null
  const allowed = new Set()
  if (typeof days[0] === 'boolean') {
    days.forEach((val, idx) => {
      if (val) allowed.add(idx)
    })
  } else {
    days.forEach(d => allowed.add(d))
  }
  return allowed
})

function hasSlotsForDate(iso) {
  const byDate = slotsByDate.value?.slots_by_date
  if (!byDate) return null
  const slots = byDate[iso]
  if (!Array.isArray(slots)) return false
  return slots.length > 0
}

function monthLabel(d) {
  const fmt = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' })
  return fmt.format(d)
}

function addMonths(base, delta) {
  return new Date(base.getFullYear(), base.getMonth() + delta, 1)
}

function isSameDayISO(aISO, bISO) {
  return aISO && bISO && aISO === bISO
}

// Monday-based index: Mon=0..Sun=6
function mondayIndex(jsDay) {
  return (jsDay + 6) % 7
}

const calCells = computed(() => {
  const first = new Date(calMonth.value.getFullYear(), calMonth.value.getMonth(), 1)
  const daysInMonth = new Date(calMonth.value.getFullYear(), calMonth.value.getMonth() + 1, 0).getDate()
  const lead = mondayIndex(first.getDay())
  const cells = []

  const weekdaySet = allowedWeekdaySet.value
  const byDate = slotsByDate.value?.slots_by_date ?? null

  for (let i = 0; i < lead; i++) cells.push({ type: 'empty', key: `e-${i}` })

  for (let day = 1; day <= daysInMonth; day++) {
    const d = new Date(calMonth.value.getFullYear(), calMonth.value.getMonth(), day)
    const iso = toISODate(d)
    const weekday = d.getDay() // 0..6

    let isAllowedDay = true
    if (weekdaySet) {
      isAllowedDay = weekdaySet.has(weekday)
    }

    let hasSlotsFlag = true
    if (byDate) {
      hasSlotsFlag = hasSlotsForDate(iso) === true
    }

    const disabled = !isAllowedDay || !hasSlotsFlag

    cells.push({
      type: 'day',
      key: iso,
      iso,
      day,
      isToday: iso === toISODate(new Date()),
      isSelected: isSameDayISO(iso, selectedDateISO.value),
      isDisabled: disabled,
    })
  }

  while (cells.length < 42) cells.push({ type: 'empty', key: `t-${cells.length}` })

  return cells.map(c => {
    if (c.type !== 'day') return c
    return { ...c, isSelected: isSameDayISO(c.iso, selectedDateISO.value) }
  })
})

// Slots for the currently selected date from backend
const slotsForSelectedDate = computed(() => {
  const data = availableSlots.value
  if (!data || !Array.isArray(data.slots)) return []
  if (selectedDateISO.value && data.date && data.date !== selectedDateISO.value) return []
  return data.slots
})

const labeledSlotsForSelectedDate = computed(() => {
  const tz = meetingForm.value.time_zone
  if (!tz) return []
  return slotsForSelectedDate.value.map(slot => {
    const key = `${slot.start}|${slot.end}`
    const startParts = utcToLocalParts(slot.start, tz)
    const labels = formatSlotLabel(slot.start, slot.end, tz)
    return {
      key,
      raw: slot,
      startHHMM: startParts?.hhmm ?? '',
      startMinutes: startParts?.hhmm ? hhmmToMinutes(startParts.hhmm) : 0,
      ...labels,
    }
  })
})

const morningSlotItems = computed(() =>
  labeledSlotsForSelectedDate.value.filter(s => s.startMinutes < 12 * 60),
)

const afternoonSlotItems = computed(() =>
  labeledSlotsForSelectedDate.value.filter(s => s.startMinutes >= 12 * 60),
)

function selectDate(iso) {
  if (!iso) return

  selectedDateISO.value = iso
  selectedSlotKey.value = ''
  meetingForm.value.start_time_local = ''
  meetingForm.value.end_time_local = ''

  // Day-specific slots will be refreshed via watcher
}

function onSlotClick(slotItem) {
  scheduleStore.$patch({ error: null })
  submitSuccess.value = ''

  if (!selectedDateISO.value) {
    scheduleStore.$patch({ error: 'Please select a date first.' })
    return
  }

  selectedSlotKey.value = slotItem.key
  meetingForm.value.start_time_local = slotItem.startLocalISO
  meetingForm.value.end_time_local = slotItem.endLocalISO
}

// Fetch helpers
async function refreshMonthSlots() {
  const tz = meetingForm.value.time_zone
  if (!tz) return

  const monthStart = new Date(calMonth.value.getFullYear(), calMonth.value.getMonth(), 1)
  const monthEnd = new Date(calMonth.value.getFullYear(), calMonth.value.getMonth() + 1, 0)

  try {
    await scheduleStore.getAvailableSlotsRange(toISODate(monthStart), toISODate(monthEnd), tz)
  } catch {
    // error handled in store
  }
}

async function refreshDaySlots() {
  const tz = meetingForm.value.time_zone
  const date = selectedDateISO.value
  if (!tz || !date) return

  try {
    await scheduleStore.getAvailableSlots(date, tz)
  } catch {
    // error handled in store
  }
}

// -----------------------------
// Combobox behavior (FIXED)
// -----------------------------
function tzOpen() {
  isTzOpen.value = true

  // highlight selected timezone if it's in filtered list
  const selected = meetingForm.value.time_zone
  let idx = filteredTimeZones.value.indexOf(selected)

  // If user typed something, try to find the first exact label match
  if (idx < 0) {
    const typedLabel = tzQuery.value.trim().toLowerCase()
    if (typedLabel) {
      idx = filteredTimeZones.value.findIndex(tz => formatTimeZoneLabel(tz).toLowerCase() === typedLabel)
    }
  }

  tzActiveIndex.value = idx >= 0 ? idx : (filteredTimeZones.value.length ? 0 : -1)
  nextTick(() => scrollTzIntoView())
}

function tzClose() {
  isTzOpen.value = false
  tzActiveIndex.value = -1
}

function tzToggle() {
  if (isTzOpen.value) tzClose()
  else tzOpen()
}

function tzSelect(rawTz) {
  // ✅ store real IANA value for backend + conversion
  meetingForm.value.time_zone = rawTz

  // ✅ show pretty label in input (no underscores)
  tzQuery.value = formatTimeZoneLabel(rawTz)

  tzClose()
}

// ✅ NEW: select on mousedown so blur never wins
function tzSelectOnMouseDown(rawTz, e) {
  // prevent focus from leaving input (prevents blur)
  e?.preventDefault?.()
  tzSelect(rawTz)
}

function tzOnInput(e) {
  const val = e?.target?.value ?? ''
  tzQuery.value = val

  if (!isTzOpen.value) tzOpen()

  tzActiveIndex.value = filteredTimeZones.value.length ? 0 : -1
  nextTick(() => scrollTzIntoView())
}

function tzOnFocus() {
  tzOpen()
}

function tzOnBlur(e) {
  // ✅ If blur is caused by clicking inside the tz widget, don't revert anything.
  const related = e?.relatedTarget
  if (related && tzWrapRef.value && tzWrapRef.value.contains(related)) {
    return
  }

  // commit what user typed if it's valid (label or raw)
  const typed = tzQuery.value.trim()

  if (!typed) {
    tzQuery.value = formatTimeZoneLabel(meetingForm.value.time_zone)
    return
  }

  // typed raw IANA
  if (timeZones.value.includes(typed)) {
    meetingForm.value.time_zone = typed
    tzQuery.value = formatTimeZoneLabel(typed)
    return
  }

  // typed label -> convert to IANA
  const candidate = labelToIana(typed)
  if (candidate && timeZones.value.includes(candidate)) {
    meetingForm.value.time_zone = candidate
    tzQuery.value = formatTimeZoneLabel(candidate)
    return
  }

  // revert display to last valid selection
  tzQuery.value = formatTimeZoneLabel(meetingForm.value.time_zone)
}

function tzOnKeydown(e) {
  if (!isTzOpen.value && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
    e.preventDefault()
    tzOpen()
    return
  }

  if (!isTzOpen.value) return

  if (e.key === 'Escape') {
    e.preventDefault()
    tzClose()
    return
  }

  if (e.key === 'ArrowDown') {
    e.preventDefault()
    if (!filteredTimeZones.value.length) return
    tzActiveIndex.value = Math.min(tzActiveIndex.value + 1, filteredTimeZones.value.length - 1)
    scrollTzIntoView()
    return
  }

  if (e.key === 'ArrowUp') {
    e.preventDefault()
    if (!filteredTimeZones.value.length) return
    tzActiveIndex.value = Math.max(tzActiveIndex.value - 1, 0)
    scrollTzIntoView()
    return
  }

  if (e.key === 'Enter') {
    // pick highlighted
    if (tzActiveIndex.value >= 0 && filteredTimeZones.value[tzActiveIndex.value]) {
      e.preventDefault()
      tzSelect(filteredTimeZones.value[tzActiveIndex.value])
      return
    }

    // or commit typed (label/raw)
    const typed = tzQuery.value.trim()
    if (!typed) return

    if (timeZones.value.includes(typed)) {
      e.preventDefault()
      tzSelect(typed)
      return
    }

    const candidate = labelToIana(typed)
    if (candidate && timeZones.value.includes(candidate)) {
      e.preventDefault()
      tzSelect(candidate)
    }
  }
}

function scrollTzIntoView() {
  const listEl = tzListRef.value
  if (!listEl) return
  const idx = tzActiveIndex.value
  if (idx < 0) return

  const item = listEl.querySelector(`[data-tz-idx="${idx}"]`)
  if (!item) return

  const itemTop = item.offsetTop
  const itemBottom = itemTop + item.offsetHeight
  const viewTop = listEl.scrollTop
  const viewBottom = viewTop + listEl.clientHeight

  if (itemTop < viewTop) listEl.scrollTop = itemTop
  else if (itemBottom > viewBottom) listEl.scrollTop = itemBottom - listEl.clientHeight
}

function onDocPointerDown(e) {
  // ✅ only care if dropdown is open
  if (!isTzOpen.value) return
  if (!tzWrapRef.value) return
  if (tzWrapRef.value.contains(e.target)) return
  tzClose()
}

// -----------------------------
// Open / Close modal (AUTO PICK NEXT AVAILABLE DAY)
// -----------------------------
function openContactForm() {
  scheduleStore.$patch({ error: null, success: false })
  submitSuccess.value = ''
  isContactModalOpen.value = true

  // Show pretty label in input whenever modal opens
  tzQuery.value = formatTimeZoneLabel(meetingForm.value.time_zone)

  // Ensure availability and calendar slots are loaded for the visible month
  scheduleStore.getAvailabilitySettings().then(() => {
    refreshMonthSlots()
  }).catch(() => {})

  // Auto-select next available day (respects backend days_available) so times can load
  if (!selectedDateISO.value) {
    const iso = nextAvailableDayISO(new Date())
    selectedDateISO.value = iso

    const d = new Date(`${iso}T00:00:00`)
    calMonth.value = new Date(d.getFullYear(), d.getMonth(), 1)
  } else {
    // Keep calendar month in sync with selected date and refresh that month's slots
    const d = new Date(`${selectedDateISO.value}T00:00:00`)
    calMonth.value = new Date(d.getFullYear(), d.getMonth(), 1)
    refreshMonthSlots()
  }
}

function closeContactForm() {
  isContactModalOpen.value = false
}

// -----------------------------
// Validation + submit
// -----------------------------
function validateMeetingForm() {
  scheduleStore.$patch({ error: null })
  submitSuccess.value = ''

  const f = meetingForm.value
  if (!f.name?.trim()) return 'Name is required.'
  if (!f.email?.trim()) return 'Email is required.'
  if (!/^\S+@\S+\.\S+$/.test(f.email.trim())) return 'Please enter a valid email.'

  if (!selectedDateISO.value) return 'Please select a date.'
  if (!selectedSlotKey.value) return 'Please choose a time slot.'

  if (!f.time_zone?.trim()) return 'Time zone is required.'
  if (!timeZones.value.includes(f.time_zone.trim()))
    return 'Time zone must match a valid IANA time zone from the list.'

  // Message is optional per API, but we can keep validation if needed
  // if (!f.message?.trim()) return 'Message is required.'

  return ''
}

async function handleMeetingSubmit() {
  const err = validateMeetingForm()
  if (err) {
    scheduleStore.$patch({ error: err })
    return
  }

  const f = meetingForm.value
  const tz = f.time_zone.trim()

  const slotItem = labeledSlotsForSelectedDate.value.find(s => s.key === selectedSlotKey.value)
  if (!slotItem) {
    scheduleStore.$patch({ error: 'Please choose a valid time slot.' })
    return
  }

  scheduleStore.name = f.name.trim()
  scheduleStore.email = f.email.trim()
  scheduleStore.start_time = slotItem.startLocalISO
  scheduleStore.end_time = slotItem.endLocalISO
  scheduleStore.time_zone = tz
  scheduleStore.message = f.message?.trim() ?? ''

  try {
    await scheduleStore.scheduleMeeting()
    const keepTimeZone = meetingForm.value.time_zone

    meetingForm.value = {
      name: '',
      email: '',
      start_time_local: '',
      end_time_local: '',
      time_zone: keepTimeZone,
      message: '',
    }

    selectedSlotKey.value = ''

    tzQuery.value = formatTimeZoneLabel(meetingForm.value.time_zone)
    submitSuccess.value = 'Thanks! Your request is recorded.'
  } catch { }
}

// Close modal with ESC
function onKeydown(e) {
  if (e.key === 'Escape' && isContactModalOpen.value) closeContactForm()
}

watch(isContactModalOpen, v => {
  if (v) {
    document.addEventListener('keydown', onKeydown)

    // ✅ FIX: use bubble phase (no `true`) so selection click isn't killed early
    document.addEventListener('pointerdown', onDocPointerDown)

    document.body.style.overflow = 'hidden'
  } else {
    document.removeEventListener('keydown', onKeydown)
    document.removeEventListener('pointerdown', onDocPointerDown)
    document.body.style.overflow = ''
    tzClose()
  }
})

// Methods
function activateFeature(featureId) {
  if (activeFeature.value === featureId) return
  activeFeature.value = featureId

  shouldAnimate.value = false
  nextTick(() => {
    void featureCard.value?.offsetWidth
    shouldAnimate.value = true
  })
}

function isFeatureActive(featureId) {
  return activeFeature.value === featureId
}

function getFeatureClasses(featureId) {
  const isActive = isFeatureActive(featureId)
  return {
    'bg-white': isActive,
    'bg-[#fcfcfc]': !isActive,
    'border-brand': isActive,
    'border-slate-200 hover:bg-slate-50': !isActive,
    'shadow-xl shadow-brand/10': isActive,
    'scale-[1.01]': isActive,
    'transform transition-all duration-300': true,
  }
}

function getSectionIdFromElement(element) {
  let current = element
  while (current && current.tagName !== 'BODY') {
    const id = current.id
    if (id && Object.prototype.hasOwnProperty.call(isVisible.value, id)) {
      return id
    }
    current = current.parentElement
  }
  return null
}

function handleIntersection(entries, observer) {
  entries.forEach(entry => {
    if (entry.isIntersecting) {
      const sectionId = getSectionIdFromElement(entry.target)

      if (sectionId && Object.prototype.hasOwnProperty.call(isVisible.value, sectionId)) {
        isVisible.value[sectionId] = true
      }

      observer.unobserve(entry.target)
    }
  })
}

onMounted(() => {
  activateFeature(DEFAULT_FEATURE)

  const revealEls = document.querySelectorAll('.reveal')
  const observer = new IntersectionObserver(handleIntersection, { threshold: INTERSECTION_THRESHOLD })
  revealEls.forEach(el => observer.observe(el))

  const zones = getAllTimeZones().slice().sort((a, b) => a.localeCompare(b))
  timeZones.value = zones

  let browserTz = ''
  try {
    browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone || ''
  } catch {
    browserTz = ''
  }

  if (browserTz && timeZones.value.includes(browserTz)) {
    meetingForm.value.time_zone = browserTz
  } else if (!timeZones.value.includes(meetingForm.value.time_zone)) {
    meetingForm.value.time_zone = timeZones.value[0] || 'UTC'
  }

  // ✅ show pretty label
  tzQuery.value = formatTimeZoneLabel(meetingForm.value.time_zone)

  // Hydrate availability data from backend for the detected time zone
  scheduleStore.getAvailabilitySettings().catch(() => { })
  void refreshMonthSlots()
})

onUnmounted(() => { })

// Keep slots in sync when month, selected date, or time zone changes
watch(
  () => toISODate(calMonth.value),
  () => {
    void refreshMonthSlots()
  },
)

watch(
  () => selectedDateISO.value,
  () => {
    selectedSlotKey.value = ''
    meetingForm.value.start_time_local = ''
    meetingForm.value.end_time_local = ''
    void refreshDaySlots()
  },
)

// When selected timezone changes (only on selection/blur commit), refresh availability
watch(
  () => meetingForm.value.time_zone,
  () => {
    // keep search input aligned if dropdown is closed
    if (!isTzOpen.value) tzQuery.value = formatTimeZoneLabel(meetingForm.value.time_zone)
    selectedSlotKey.value = ''
    meetingForm.value.start_time_local = ''
    meetingForm.value.end_time_local = ''
    void refreshMonthSlots()
    void refreshDaySlots()
  },
)
</script>

<template>
  <main class="pt-0 w-full" :style="{ '--bg-pattern': 'url(' + bg + ')' }">
    <section id="hero"
      class="bg-[#f1f1f4] border-b border-slate-100 snap-start min-h-[90vh] flex items-center relative overflow-hidden hero-background-pattern">
      <div class="absolute inset-0 bg-white/75 backdrop-blur-[1px] z-0"></div>

      <div class="mx-auto max-w-6xl px-4 py-16 md:py-20 lg:py-24 text-center relative z-10">
        <div class="mb-10">
          <p class="text-xl font-bold uppercase tracking-[0.3em] text-secondary mb-2">Comprehensive</p>
          <p
            class="heading-font text-4xl md:text-6xl font-extrabold text-brand tracking-tight leading-none animate-fade-in-up delay-100">
            Facility Management System
          </p>
        </div>

        <p class="text-base md:text-xl text-slate-700 max-w-3xl mx-auto mb-12 animate-fade-in-up delay-300">
          Streamline your facility operations with our all-in-one platform. Manage work orders, contractors, and
          maintenance with ease.
        </p>

        <div class="flex justify-center gap-5 mb-14 animate-fade-in-up delay-400">
          <a href="#contact" class="group">
            <button
              class="rounded-full bg-brand px-10 py-3.5 text-base font-bold text-white shadow-xl shadow-brand/40 transition duration-300 group-hover:scale-[1.05] group-hover:shadow-2xl">
              Get Started
            </button>
          </a>
          <a href="#features" class="group">
            <button
              class="rounded-full bg-white border border-slate-200 px-10 py-3.5 text-base font-semibold text-secondary shadow-lg hover:bg-slate-50 transition duration-300 group-hover:scale-[1.05]">
              Learn More
            </button>
          </a>
        </div>

        <div class="rounded-3xl shadow-2xl hero-image-parallax reveal delay-500"
          :class="{ 'is-visible': isVisible.features }">
          <img :src="analyticsSample" alt="Facility Management Dashboard Analytics"
            class="w-full object-cover shadow-inner" />
        </div>
      </div>
    </section>

    <section id="features"
      class="bg-secondary/10 border-b border-slate-100 py-24 lg:py-32 snap-start min-h-screen flex items-center">
      <div class="mx-auto max-w-6xl px-4 w-full">
        <div class="text-center mb-16 reveal" :class="{ 'is-visible': isVisible.features }">
          <p class="text-xl font-bold uppercase tracking-[0.3em] text-secondary mb-2">Powerful Features of</p>
          <h2 class="heading-font text-4xl md:text-5xl font-extrabold text-secondary mb-4">
            <span class="text-brand">Facility Management System</span>
          </h2>
          <p class="text-slate-600 max-w-3xl mx-auto text-xl">
            Built to align operations, maintenance, and compliance in one unified workspace from daily work orders to
            long-term portfolio planning.
          </p>
        </div>

        <div class="grid gap-14 md:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] items-start">
          <div class="flex flex-col gap-4 reveal" :class="{ 'is-visible': isVisible.features }">
            <button v-for="feature in features" :key="feature.id"
              class="feature-item w-full text-left rounded-xl border px-6 py-4 transition-all duration-300 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand/30"
              :class="getFeatureClasses(feature.id)" @click="activateFeature(feature.id)"
              @mouseenter="activateFeature(feature.id)" @focus="activateFeature(feature.id)" :data-image="feature.id">
              <div class="flex items-center gap-4">
                <div class="text-brand text-2xl flex-shrink-0" :class="feature.icon"></div>
                <span class="text-base font-extrabold uppercase tracking-[0.1em]"
                  :class="isFeatureActive(feature.id) ? 'text-brand-dark' : 'text-secondary/80'">
                  {{ feature.title }}
                </span>
              </div>
              <p class="feature-body mt-2 text-sm text-slate-600 transition-all duration-300 ease-in-out px-1"
                :class="isFeatureActive(feature.id) ? 'opacity-100 max-h-40 pt-2' : 'opacity-0 max-h-0 overflow-hidden'">
                {{ feature.description }}
              </p>
            </button>
          </div>

          <div class="reveal md:sticky md:top-12" :class="{ 'is-visible': isVisible.features }">
            <div id="feature-card" ref="featureCard"
              class="rounded-3xl bg-white shadow-2xl border border-slate-100 p-4 lg:p-6"
              :class="{ 'animate-featureSlideCard': shouldAnimate }">
              <div class="relative overflow-hidden rounded-2xl border border-slate-100 bg-slate-900/95 shadow-inner">
                <img v-for="feature in features" :key="feature.id" :src="feature.image" :alt="feature.alt"
                  class="feature-image w-full transition-opacity duration-500"
                  :class="{ 'opacity-100': isFeatureActive(feature.id), 'hidden opacity-0': !isFeatureActive(feature.id) }"
                  :data-image-id="feature.id" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <div
      class="w-full bg-fixed bg-cover bg-center parallax-bg relative px-4 min-h-[12vh] flex items-center justify-center py-6">
      <div class="absolute inset-0 bg-secondary/20 z-0"></div>
      <p class="text-center heading-font text-2xl md:text-5xl font-extrabold text-white relative z-10 drop-shadow-lg">
        "<span class="text-brand">ONE PLATFORM</span>, EVERY FACILITY UNDER CONTROL."
      </p>
    </div>

    <section id="benefits" class="bg-white border-b border-slate-100 py-0 snap-start min-h-screen flex items-center">
      <div class="mx-auto max-w-6xl px-4 w-full py-24 lg:py-32">
        <div class="text-center mb-16 reveal" :class="{ 'is-visible': isVisible.benefits }">
          <h2 class="heading-font text-4xl md:text-5xl font-extrabold text-secondary mb-4">
            Why choose our <span class="text-brand"><br />Facility Management System?</span>
          </h2>
          <p class="text-slate-600 max-w-3xl mx-auto text-xl">
            Connect strategy, operations, and on-the-ground teams so your facilities can scale without chaos.
          </p>
        </div>

        <div class="grid gap-12 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)] items-center reveal"
          :class="{ 'is-visible': isVisible.benefits }">
          <div>
            <h3 class="heading-font text-2xl md:text-3xl font-bold text-secondary mb-6">Why Choose Our FMS Platform?
            </h3>
            <ul class="space-y-4 text-base text-slate-700">
              <li v-for="benefit in benefits" :key="benefit"
                class="flex items-start gap-4 p-3 bg-slate-50 rounded-lg shadow-sm">
                <span
                  class="flex-shrink-0 mt-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-success-light/20 text-success-dark">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24"
                    stroke="currentColor" stroke-width="2.5">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </span>
                <span>{{ benefit }}</span>
              </li>
            </ul>
          </div>

          <div class="flex justify-center lg:justify-end">
            <div
              class="relative w-full max-w-md rounded-3xl bg-gradient-to-tr from-brand to-success-light px-8 py-10 text-center text-white shadow-xl">
              <div class="mx-auto mb-5 flex h-10 w-10 items-center justify-center rounded-2xl bg-white/10">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24"
                  stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M4 19h16M5 11l4 4L19 7" />
                </svg>
              </div>
              <h3 class="heading-font text-lg font-semibold mb-1">Data-Driven Insights</h3>
              <p class="text-sm text-white/80 max-w-xs mx-auto">
                Get comprehensive analytics on facility utilization, maintenance performance, and operational
                efficiency.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <div class="w-full bg-[#f1f1f4] px-4 min-h-[12vh] flex items-center justify-center py-4">
      <p class="text-center heading-font text-xl md:text-3xl font-extrabold text-secondary/80">"DESIGNED TO EMPOWER
        BUSINESS PERFORMANCE."</p>
    </div>

    <section id="contact"
      class="bg-slate-50 py-0 snap-start min-h-screen flex flex-col relative contact-background-pattern">
      <div class="absolute inset-0 bg-gradient-to-r from-slate-50/95 to-slate-50/70 z-0"></div>

      <div class="relative z-10 w-full flex justify-center pt-6 pb-4 md:pt-8 md:pb-6">
        <a href="/" class="group block w-auto cursor-pointer flex flex-col items-center">
          <img :src="logo" alt="Company Logo Icon"
            class="h-16 md:h-20 w-auto transform transition duration-300 group-hover:scale-110 mb-2" />
          <span
            class="text-2xl md:text-4xl font-extrabold tracking-widest uppercase text-brand transform transition duration-300 group-hover:scale-105">
            MARBLE SOLUTION
          </span>
        </a>
      </div>

      <div class="mx-auto max-w-6xl px-4 py-8 md:py-12 lg:py-24 flex justify-center w-full relative z-10 flex-1">
        <div class="reveal text-center w-full" :class="{ 'is-visible': isVisible.contact }">
          <p class="text-sm md:text-base font-bold uppercase tracking-[0.2em] text-brand/80 mb-3 md:mb-4">Ready to
            transform your operations?</p>

          <h2
            class="heading-font text-2xl md:text-4xl lg:text-5xl font-extrabold text-secondary mb-4 md:mb-5 leading-tight px-2">
            Bring every facility into
            <span class="text-brand block md:inline"><br class="hidden md:block" />one connected system.</span>
          </h2>

          <p class="text-slate-600 mb-6 md:mb-8 max-w-xl mx-auto text-base md:text-lg px-4">
            Share a few details and our team will prepare a tailored walk-through of how the platform supports your
            sites, people, and portfolio.
          </p>

          <ul class="space-y-2 md:space-y-3 text-xs md:text-sm text-slate-700 max-w-md mx-auto px-4">
            <li v-for="(point, index) in contactPoints" :key="index"
              class="flex items-start gap-2 md:gap-3 p-2.5 md:p-3 rounded-lg bg-white shadow-md border border-slate-100 text-left">
              <span
                :class="['flex-shrink-0 mt-0.5 md:mt-1 h-2.5 w-2.5 md:h-3 md:w-3 rounded-full', point.color, 'shadow']"></span>
              <span class="leading-relaxed">{{ point.text }}</span>
            </li>
          </ul>

          <div class="group block mt-6 md:mt-10 px-4">
            <button type="button" @click="openContactForm"
              class="rounded-full bg-brand px-8 md:px-12 py-3 md:py-4 text-base md:text-xl font-bold text-white shadow-xl shadow-brand/40 transition duration-300 hover:scale-[1.05] hover:shadow-2xl w-full max-w-xs">
              Request Demo
            </button>
          </div>
        </div>
      </div>
    </section>

    <LandingFooter />

    <!-- Contact Modal -->
    <Teleport to="body">
      <div v-if="isContactModalOpen" class="fixed inset-0 z-[9999]">
        <div class="absolute inset-0 bg-black/40 backdrop-blur-[2px]" @click="closeContactForm"></div>

        <div class="relative h-full w-full overflow-y-auto p-4">
          <div class="min-h-full w-full flex items-start justify-center">
            <div
              class="w-full max-w-2xl rounded-3xl bg-white shadow-2xl border border-slate-100 overflow-hidden max-h-[calc(100vh-2rem)] flex flex-col"
              role="dialog" aria-modal="true" aria-label="Schedule meeting">
              <div class="px-6 py-5 md:px-8 md:py-6 border-b border-slate-100 flex items-start justify-between gap-4">
                <div>
                  <p class="text-xs font-bold uppercase tracking-[0.2em] text-brand/80">Marble Solution</p>
                  <h3 class="heading-font text-xl md:text-2xl font-extrabold text-secondary">Request Demo Meeting</h3>
                </div>

                <button type="button" @click="closeContactForm"
                  class="h-10 w-10 rounded-full bg-slate-100 hover:bg-slate-200 transition flex items-center justify-center text-slate-700"
                  aria-label="Close">
                  ✕
                </button>
              </div>

              <form class="px-6 py-6 md:px-8 md:py-8 overflow-y-auto" @submit.prevent="handleMeetingSubmit">
                <div class="grid gap-5 md:grid-cols-2">
                  <div class="md:col-span-1">
                    <label class="block text-sm font-semibold text-secondary mb-3">Name</label>
                    <input v-model="meetingForm.name" type="text"
                      class="w-full rounded-xl bg-white px-4 py-3 text-sm outline-none !border !border-slate-400 ring-1 ring-inset ring-slate-200 focus:!border-brand focus:ring-4 focus:ring-brand/20" />
                  </div>

                  <div class="md:col-span-1">
                    <label class="block text-sm font-semibold text-secondary mb-3">Email</label>
                    <input v-model="meetingForm.email" type="email"
                      class="w-full rounded-xl bg-white px-4 py-3 text-sm outline-none !border !border-slate-400 ring-1 ring-inset ring-slate-200 focus:!border-brand focus:ring-4 focus:ring-brand/20" />
                  </div>

                  <!-- SCHEDULER -->
                  <div class="md:col-span-2 mt-3">
                    <div class="flex items-center justify-between gap-3">
                      <label class="block text-sm font-semibold text-secondary">Select Your Available Time</label>
                    </div>

                    <div class="mt-4 rounded-2xl border border-slate-200 bg-white p-4">
                      <div class="flex items-center justify-between">
                        <button type="button"
                          class="text-sm font-semibold text-slate-600 hover:text-secondary transition flex items-center gap-2"
                          @click="calMonth = addMonths(calMonth, -1)">
                          <span class="text-base">‹</span> Prev Month
                        </button>

                        <p class="text-sm font-extrabold text-secondary">
                          {{ monthLabel(calMonth) }}
                        </p>

                        <button type="button"
                          class="text-sm font-semibold text-slate-600 hover:text-secondary transition flex items-center gap-2"
                          @click="calMonth = addMonths(calMonth, 1)">
                          Next Month <span class="text-base">›</span>
                        </button>
                      </div>

                      <div class="mt-4 grid grid-cols-7 text-xs font-bold text-slate-500">
                        <div class="py-2 text-center">MON</div>
                        <div class="py-2 text-center">TUE</div>
                        <div class="py-2 text-center">WED</div>
                        <div class="py-2 text-center">THU</div>
                        <div class="py-2 text-center">FRI</div>
                        <div class="py-2 text-center text-rose-500">SAT</div>
                        <div class="py-2 text-center text-rose-500">SUN</div>
                      </div>

                      <div class="grid grid-cols-7 gap-2">
                        <template v-for="cell in calCells" :key="cell.key">
                          <div v-if="cell.type === 'empty'" class="h-10"></div>

                          <button v-else type="button" class="h-10 rounded-xl border text-sm font-semibold transition"
                            :disabled="cell.isDisabled" :class="[
                              cell.isDisabled
                                ? 'bg-brand/25 text-slate-400 border-slate-200 cursor-not-allowed'
                                : cell.isSelected
                                  ? 'bg-slate-800 text-white border-slate-800'
                                  : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50',
                              cell.isToday && !cell.isSelected && !cell.isDisabled ? 'ring-2 ring-brand/25' : '',
                            ]" @click="selectDate(cell.iso)">
                            {{ cell.day }}
                          </button>
                        </template>
                      </div>

                      <div class="mt-6">
                        <p class="text-sm font-semibold text-secondary mb-1">Available times</p>
                        <p class="text-xs text-slate-500 mb-3">Times are based on Brunei (Asia/Brunei).</p>

                        <div v-if="!selectedDateISO" class="text-xs text-slate-500">
                          Please select a date to see available time slots.
                        </div>

                        <div v-else-if="!labeledSlotsForSelectedDate.length" class="text-xs text-slate-500">
                          No available slots for this date in your selected time zone.
                        </div>

                        <div v-else class="space-y-5">
                          <div v-if="morningSlotItems.length">
                            <p class="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">
                              Morning
                            </p>
                            <div class="grid grid-cols-2 md:grid-cols-3 gap-3">
                              <button v-for="slot in morningSlotItems" :key="slot.key" type="button"
                                class="rounded-xl border px-3 py-3 text-sm font-semibold transition"
                                :class="[
                                  selectedSlotKey === slot.key
                                    ? 'bg-slate-800 text-white border-slate-800'
                                    : 'bg-rose-50/60 text-slate-700 border-rose-100 hover:bg-rose-50',
                                ]" @click="onSlotClick(slot)">
                                <span class="inline-flex flex-col items-center leading-tight">
                                  <span>{{ slot.timeLabel }}</span>
                                </span>
                              </button>
                            </div>
                          </div>

                          <div v-if="afternoonSlotItems.length">
                            <p class="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">
                              Afternoon
                            </p>
                            <div class="grid grid-cols-2 md:grid-cols-3 gap-3">
                              <button v-for="slot in afternoonSlotItems" :key="slot.key" type="button"
                                class="rounded-xl border px-3 py-3 text-sm font-semibold transition"
                                :class="[
                                  selectedSlotKey === slot.key
                                    ? 'bg-slate-800 text-white border-slate-800'
                                    : 'bg-rose-50/60 text-slate-700 border-rose-100 hover:bg-rose-50',
                                ]" @click="onSlotClick(slot)">
                                <span class="inline-flex flex-col items-center leading-tight">
                                  <span>{{ slot.timeLabel }}</span>
                                </span>
                              </button>
                            </div>
                          </div>
                        </div>

                        <!-- Time Zone (auto-detected, not editable) -->
                        <div class="mt-7 pt-5 border-t border-slate-200">
                          <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                            <label class="text-sm font-semibold text-secondary">
                              Time Zone
                              <span class="ml-1 text-xs font-normal text-slate-500">(auto-detected)</span>
                            </label>

                            <div class="relative w-full md:w-[360px]">
                              <input :value="formatTimeZoneLabel(meetingForm.time_zone)" type="text" disabled
                                class="w-full rounded-xl bg-slate-50 px-4 py-2.5 text-sm outline-none !border !border-slate-200 text-slate-600 cursor-not-allowed" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="md:col-span-2">
                    <label class="block text-sm font-semibold text-secondary mb-3 mt-3">Message <span class="text-xs text-slate-500 font-normal">(optional)</span></label>
                    <textarea v-model="meetingForm.message" rows="4"
                      class="w-full rounded-xl bg-white px-4 py-3 text-sm outline-none !border !border-slate-400 ring-1 ring-inset ring-slate-200 focus:!border-brand focus:ring-4 focus:ring-brand/20 resize-none" />
                  </div>
                </div>

                <div v-if="scheduleError"
                  class="mt-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {{ scheduleError }}
                </div>

                <div v-if="submitSuccess"
                  class="mt-5 rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
                  {{ submitSuccess }}
                </div>

                <div class="mt-7 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
                  <p class="text-xs text-slate-500"></p>

                  <div class="flex gap-3 justify-end">
                    <button type="button" @click="closeContactForm"
                      class="rounded-full bg-white border border-slate-200 px-6 py-3 text-sm font-semibold text-secondary shadow-sm hover:bg-slate-50 transition">
                      Cancel
                    </button>

                    <button type="submit" :disabled="isSubmitting"
                      class="rounded-full bg-brand px-8 py-3 text-sm font-bold text-white shadow-xl shadow-brand/30 transition duration-300 hover:scale-[1.02] disabled:opacity-60 disabled:hover:scale-100">
                      {{ isSubmitting ? 'Submitting...' : 'Submit Request' }}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </main>
</template>

<style scoped>
main {
  padding: 0;
  margin: 0;
  inline-size: 100%;
}

.heading-font {
  font-family: inherit;
}

.reveal {
  opacity: 0;
  transform: translateY(40px);
  transition: opacity 0.8s cubic-bezier(0.2, 0.5, 0.2, 1), transform 0.8s cubic-bezier(0.2, 0.5, 0.2, 1);
}

.reveal.is-visible {
  opacity: 1;
  transform: translateY(0);
}

@keyframes feature-slide-card {
  0% {
    opacity: 0;
    transform: translateX(50px);
  }

  100% {
    opacity: 1;
    transform: translateX(0);
  }
}

.animate-featureSlideCard {
  animation: feature-slide-card 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
}

@keyframes fade-in-up {
  0% {
    opacity: 0;
    transform: translateY(30px);
  }

  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-in-up {
  animation: fade-in-up 0.9s cubic-bezier(0.165, 0.84, 0.44, 1) forwards;
  opacity: 0;
}

.animate-fade-in-up.delay-100 {
  animation-delay: 0.1s;
}

.animate-fade-in-up.delay-200 {
  animation-delay: 0.2s;
}

.animate-fade-in-up.delay-300 {
  animation-delay: 0.3s;
}

.animate-fade-in-up.delay-400 {
  animation-delay: 0.4s;
}

.animate-fade-in-up.delay-500 {
  animation-delay: 0.5s;
}

.parallax-bg {
  background-attachment: fixed;
  background-image: url('https://images.unsplash.com/photo-1542838703-9ef2101e4a52?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1700&q=80');
  background-position: center;
}

.hero-background-pattern,
.contact-background-pattern {
  background-image: var(--bg-pattern);
  background-position: center;
  background-repeat: repeat;
  background-size: cover;
}

#contact {
  position: relative;
  overflow: hidden;
}
</style>
